import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate } from './mock';
import { getProject, updateProject } from './projects';
import { listProposals, updateProposal } from './proposals';

export type VotingStatus = 'Scheduled' | 'Open' | 'Closed';
export type VotingOrderKey = 'newest' | 'closes' | 'votes';
/** `scheduled` closes at a date; `manual` stays open until closed by hand. */
export type VotingMode = 'scheduled' | 'manual';

export interface VotingPeriod {
  id: string;
  projectId: string;
  project: string;
  projectSlug: string;
  mode: VotingMode;
  opensAt: string;
  closesAt: string | null;
  votes: number;
  updatedHours: number;
  status: VotingStatus;
}

/** API shape from `GET /admin/voting-periods` (status is added by the mapper). */
export type VotingPeriodDto = Omit<VotingPeriod, 'status' | 'projectId'>;

export function mapVotingPeriod(dto: VotingPeriodDto): VotingPeriod {
  return { ...dto, projectId: dto.projectSlug, status: votingStatus(dto) };
}

/** Wire shape from `GET /admin/voting-periods` (summary adds status/votes/project). */
interface VotingPeriodApiDto {
  id: string;
  project: string;
  projectSlug: string;
  mode: VotingMode;
  opensAt: string;
  closesAt: string | null;
  votes: number;
  status: VotingStatus;
  updatedAt?: string;
}

function mapVotingPeriodApi(dto: VotingPeriodApiDto): VotingPeriod {
  return {
    id: dto.id,
    projectId: dto.projectSlug,
    project: dto.project,
    projectSlug: dto.projectSlug,
    mode: dto.mode,
    opensAt: dto.opensAt,
    closesAt: dto.closesAt,
    votes: dto.votes,
    updatedHours: dto.updatedAt ? hoursSince(dto.updatedAt) : 0,
    status: dto.status,
  };
}

export interface VotingPeriodInput {
  /** The project id (matches `POST /admin/voting-periods`). */
  projectId: string;
  mode: VotingMode;
  opensAt: string;
  closesAt?: string;
}

export interface ListVotingPeriodsParams {
  search?: string;
  status?: 'all' | VotingStatus;
  order?: VotingOrderKey;
  page?: number;
  pageSize?: number;
}

export type PaginatedVotingPeriods = Paginated<VotingPeriod>;

export const VOTING_PAGE_SIZE = 6;
export const VOTING_PAGE_SIZE_OPTIONS = [6, 12, 24, 48];

export interface VotingResult {
  artist: string;
  artistSlug: string;
  votes: number;
}

export interface VotingResults {
  project: string;
  projectSlug: string;
  totalVotes: number;
  results: VotingResult[];
}

let db: VotingPeriodDto[] = [
  {
    id: 'bloom',
    project: 'Bloom',
    projectSlug: 'bloom',
    mode: 'scheduled',
    opensAt: '2026-10-05',
    closesAt: '2026-10-12',
    votes: 1832,
    updatedHours: 2,
  },
  {
    id: 'salt-and-iron',
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    mode: 'scheduled',
    opensAt: '2026-10-11',
    closesAt: '2026-10-18',
    votes: 1264,
    updatedHours: 5,
  },
  {
    id: 'ember-field',
    project: 'Ember Field',
    projectSlug: 'ember-field',
    mode: 'manual',
    opensAt: '2026-10-18',
    closesAt: null,
    votes: 754,
    updatedHours: 24,
  },
  {
    id: 'paper-cities',
    project: 'Paper Cities',
    projectSlug: 'paper-cities',
    mode: 'scheduled',
    opensAt: '2026-09-14',
    closesAt: '2026-09-28',
    votes: 2401,
    updatedHours: 72,
  },
  {
    id: 'concrete-seasons',
    project: 'Concrete Seasons',
    projectSlug: 'concrete-seasons',
    mode: 'scheduled',
    opensAt: '2026-11-20',
    closesAt: '2026-12-03',
    votes: 0,
    updatedHours: 96,
  },
];

const resultsByProject: Record<string, VotingResult[]> = {
  bloom: [
    { artist: 'Mara Oliveira', artistSlug: 'mara-oliveira', votes: 612 },
    { artist: 'Tomas Beck', artistSlug: 'tomas-beck', votes: 384 },
    { artist: 'Priya Nair', artistSlug: 'priya-nair', votes: 271 },
    { artist: 'Jonas Feld', artistSlug: 'jonas-feld', votes: 198 },
  ],
  'salt-and-iron': [
    { artist: 'Ade Bakare', artistSlug: 'ade-bakare', votes: 540 },
    { artist: 'Jonas Feld', artistSlug: 'jonas-feld', votes: 220 },
    { artist: 'Mara Oliveira', artistSlug: 'mara-oliveira', votes: 180 },
  ],
  'ember-field': [
    { artist: 'Lena Costa', artistSlug: 'lena-costa', votes: 233 },
    { artist: 'Rui Tanaka', artistSlug: 'rui-tanaka', votes: 118 },
    { artist: 'Priya Nair', artistSlug: 'priya-nair', votes: 96 },
  ],
};

export function formatVotingDate(value: string | null): string {
  if (!value) return 'Until closed';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function votingStatus(
  period: Pick<VotingPeriod, 'opensAt' | 'closesAt'>,
  now = new Date(),
): VotingStatus {
  const opens = new Date(period.opensAt).getTime();
  if (now.getTime() < opens) return 'Scheduled';
  // Manual periods have no close date until closed by hand.
  if (period.closesAt === null) return 'Open';
  return now.getTime() > new Date(period.closesAt).getTime() ? 'Closed' : 'Open';
}

function sortPeriods(items: VotingPeriodDto[], order: VotingOrderKey): VotingPeriodDto[] {
  const closeTime = (period: VotingPeriodDto) =>
    period.closesAt ? new Date(period.closesAt).getTime() : Number.POSITIVE_INFINITY;

  const sorted = [...items];
  switch (order) {
    case 'closes':
      sorted.sort((a, b) => closeTime(a) - closeTime(b));
      break;
    case 'votes':
      sorted.sort((a, b) => b.votes - a.votes);
      break;
    default:
      sorted.sort((a, b) => a.updatedHours - b.updatedHours);
  }
  return sorted;
}

export async function listVotingPeriods(
  params: ListVotingPeriodsParams = {},
): Promise<PaginatedVotingPeriods> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin['voting-periods'].get({
      query: {
        search: params.search,
        status: params.status,
        order: params.order,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<VotingPeriodApiDto>>(response);
    return { ...page, items: page.items.map(mapVotingPeriodApi) };
  }
  await delay();
  const { search, status = 'all', order = 'newest', page, pageSize = VOTING_PAGE_SIZE } = params;
  const now = new Date();

  const term = search?.trim().toLowerCase();
  const filtered = db.filter((period) => {
    const matchesStatus = status === 'all' || votingStatus(period, now) === status;
    const matchesSearch = !term || period.project.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const result = paginate(sortPeriods(filtered, order), page, pageSize);
  return { ...result, items: result.items.map(mapVotingPeriod) };
}

export function queryVotingPeriods(
  params: ListVotingPeriodsParams = {},
): Promise<PaginatedVotingPeriods> {
  return listVotingPeriods(params);
}

export async function getVotingPeriod(id: string): Promise<VotingPeriod | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin['voting-periods'].get({ query: { pageSize: 100 } });
    const page = unwrap<Paginated<VotingPeriodApiDto>>(response);
    const found = page.items.find((period) => period.id === id || period.projectSlug === id);
    return found ? mapVotingPeriodApi(found) : undefined;
  }
  await delay();
  const dto = db.find((period) => period.id === id);
  return dto ? mapVotingPeriod(dto) : undefined;
}

export async function createVotingPeriod(input: VotingPeriodInput): Promise<VotingPeriod> {
  if (!USE_MOCK) {
    const created = unwrap<{
      id: string;
      projectId: string;
      mode: VotingMode;
      opensAt: string;
      closesAt: string | null;
    }>(
      await sdk.api.admin['voting-periods'].post({
        projectId: input.projectId,
        mode: input.mode,
        opensAt: input.opensAt || undefined,
        closesAt: input.mode === 'manual' ? undefined : input.closesAt || undefined,
      }),
    );
    return {
      id: created.id,
      projectId: created.projectId,
      project: '',
      projectSlug: created.projectId,
      mode: created.mode,
      opensAt: created.opensAt,
      closesAt: created.closesAt,
      votes: 0,
      updatedHours: 0,
      status: votingStatus(created),
    };
  }
  await delay();
  const project = await getProject(input.projectId);
  const period: VotingPeriodDto = {
    id: crypto.randomUUID(),
    project: project?.name ?? '',
    projectSlug: project?.slug ?? input.projectId,
    mode: input.mode,
    opensAt: input.opensAt,
    closesAt: input.mode === 'manual' ? null : (input.closesAt ?? null),
    votes: 0,
    updatedHours: 0,
  };
  db = [period, ...db];
  return mapVotingPeriod(period);
}

/**
 * Closes the project's open period, marks the top-voted proposal as the winner
 * (the rest as not selected) and advances the project to Winner Selected.
 */
/** Maps a raw `VotingPeriod` row (no summary fields) for mutation responses. */
function mapRawPeriod(raw: {
  id: string;
  projectId: string;
  mode: VotingMode;
  opensAt: string;
  closesAt: string | null;
}): VotingPeriod {
  return {
    id: raw.id,
    projectId: raw.projectId,
    project: '',
    projectSlug: raw.projectId,
    mode: raw.mode,
    opensAt: raw.opensAt,
    closesAt: raw.closesAt,
    votes: 0,
    updatedHours: 0,
    status: votingStatus(raw),
  };
}

export async function finalizeVoting(projectSlug: string): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.projects({ id: projectSlug }).finalize.post({});
    return;
  }
  await delay();
  const project = await getProject(projectSlug);
  if (!project) throw new Error(`Project ${projectSlug} not found`);

  const proposals = (await listProposals({ project: projectSlug, pageSize: 100 })).items;
  if (proposals.length === 0) throw new Error('Project has no proposals');

  const winner = proposals.reduce((best, entry) => (entry.votes > best.votes ? entry : best));

  for (const proposal of proposals) {
    await updateProposal(proposal.id, {
      title: proposal.title,
      project: proposal.project,
      artist: proposal.artist,
      status: proposal.id === winner.id ? 'Winner' : 'Not selected',
      description: proposal.description,
      coverMedia: proposal.coverMedia ?? null,
      gallery: proposal.gallery ?? [],
    });
  }

  await updateProject(project.id, {
    name: project.name,
    status: 'Winner Selected',
    description: project.description,
    votingStarts: '',
    votingEnds: '',
    heroMedia: project.heroMedia ?? null,
    gallery: project.gallery ?? [],
  });

  const period = db.find((entry) => entry.projectSlug === projectSlug && entry.closesAt === null);
  if (period) await closeVotingPeriod(period.id);
}

export async function closeVotingPeriod(id: string): Promise<VotingPeriod> {
  if (!USE_MOCK) {
    const raw = unwrap<{
      id: string;
      projectId: string;
      mode: VotingMode;
      opensAt: string;
      closesAt: string | null;
    }>(await sdk.api.admin['voting-periods']({ id }).close.post());
    return mapRawPeriod(raw);
  }
  await delay();
  const today = new Date().toISOString().slice(0, 10);
  let updated: VotingPeriodDto | undefined;
  db = db.map((period) => {
    if (period.id !== id) return period;
    updated = { ...period, closesAt: today, updatedHours: 0 };
    return updated;
  });
  if (!updated) throw new Error(`Voting period ${id} not found`);
  return mapVotingPeriod(updated);
}

export async function extendVotingPeriod(id: string, closesAt: string): Promise<VotingPeriod> {
  if (!USE_MOCK) {
    const raw = unwrap<{
      id: string;
      projectId: string;
      mode: VotingMode;
      opensAt: string;
      closesAt: string | null;
    }>(await sdk.api.admin['voting-periods']({ id }).patch({ closesAt }));
    return mapRawPeriod(raw);
  }
  await delay();
  let updated: VotingPeriodDto | undefined;
  db = db.map((period) => {
    if (period.id !== id) return period;
    updated = { ...period, closesAt, updatedHours: 0 };
    return updated;
  });
  if (!updated) throw new Error(`Voting period ${id} not found`);
  return mapVotingPeriod(updated);
}

export async function getVotingResults(projectSlug: string): Promise<VotingResults | undefined> {
  if (!USE_MOCK) {
    const data = unwrap<{
      projectId: string;
      totalVotes: number;
      results: Array<{ artistId: string; artistName: string; votes: number; percentage: number }>;
    }>(await sdk.api.admin.projects({ id: projectSlug }).results.get());

    return {
      project: '',
      projectSlug,
      totalVotes: data.totalVotes,
      results: data.results.map((row) => ({
        artist: row.artistName,
        artistSlug: row.artistId,
        votes: row.votes,
      })),
    };
  }
  await delay();
  const period = db.find((item) => item.projectSlug === projectSlug);
  if (!period) return undefined;

  const results = resultsByProject[projectSlug] ?? [];
  return {
    project: period.project,
    projectSlug,
    totalVotes: results.reduce((sum, result) => sum + result.votes, 0),
    results,
  };
}
